import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import './WikiItemDetail.css';
import api from '../API/Api';
import { McIcon } from '../components/CraftingGrid';

const FALLBACK_VIDEO = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4';

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const TAGS = [
  { value: 'all',         label: 'All' },
  { value: 'new_content', label: 'New Content', color: '#52c078' },
  { value: 'balance',     label: 'Balance',     color: '#f0ab20' },
  { value: 'bugfix',      label: 'Bugfix',      color: '#e84c3d' },
  { value: 'mechanic',    label: 'Mechanics',   color: '#5a9ee0' },
  { value: 'rework',      label: 'Rework',      color: '#a070e0' },
  { value: 'removed',     label: 'Removed',     color: '#9e9e9e' },
];


const IMPACT_COLOR = { minor: '#9e9e9e', major: '#f0ab20', rework: '#e84c3d' };
const STATUS_COLOR = { published: '#52c078', rolled_back: '#9e9e9e', draft: '#5a9ee0' };

function tagColor(tag) {
  return TAGS.find(t => t.value === tag)?.color || '#ccc';
}

function ImpactBadge({ impact }) {
  const c = IMPACT_COLOR[impact] || '#9e9e9e';
  return (
    <span style={{
      background: `${c}22`, color: c, border: `1px solid ${c}44`,
      borderRadius: 4, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em',
    }}>
      {(impact || 'minor').toUpperCase()}
    </span>
  );
}

function VersionCard({ v }) {
  const entries     = v.entries || [];
  const tagCounts   = entries.reduce((acc, e) => { acc[e.tag] = (acc[e.tag] || 0) + 1; return acc; }, {});
  const statusC     = STATUS_COLOR[v.status] || '#9e9e9e';

  return (
    <div style={{
      border: '1px solid var(--border-mid)', borderRadius: 12,
      background: 'var(--bg-raised)', overflow: 'hidden',
    }}>
      {(v.video_url || v.image_url) && (
        <div style={{ height: 130, overflow: 'hidden', position: 'relative' }}>
          {v.video_url ? (
            <video
              src={v.video_url}
              autoPlay loop muted playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6 }}
            />
          ) : (
            <img src={v.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 30%, var(--bg-raised))' }} />
        </div>
      )}
      <div style={{ padding: '1.2rem 1.4rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{
            background: 'var(--bg-card)', border: '1px solid var(--border-mid)',
            borderRadius: 8, padding: '0.5rem 0.75rem', minWidth: 56, textAlign: 'center',
          }}>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>{v.type}</div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text)', marginTop: '0.1rem' }}>{v.version}</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
              <ImpactBadge impact={v.impact} />
              <span style={{
                background: `${statusC}1a`, color: statusC, border: `1px solid ${statusC}33`,
                borderRadius: 4, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700,
              }}>
                {v.status || 'draft'}
              </span>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text)' }}>{v.title}</h3>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.15rem' }}>{fmtDate(v.date)}</div>
          </div>
        </div>

        {v.summary && (
          <p style={{ color: 'var(--text-secondary)', margin: '0.8rem 0 0', fontSize: '0.9rem', lineHeight: 1.6 }}>
            {v.summary}
          </p>
        )}

        {entries.length > 0 && (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.9rem' }}>
            {Object.entries(tagCounts).map(([tag, n]) => (
              <span key={tag} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                background: `${tagColor(tag)}18`, color: tagColor(tag),
                border: `1px solid ${tagColor(tag)}33`,
                borderRadius: 12, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
              }}>
                {n} {TAGS.find(t => t.value === tag)?.label || tag}
              </span>
            ))}
          </div>
        )}

        {entries.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', marginTop: '1rem' }}>
            {entries.slice(0, 4).map(e => (
              <div key={e.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                fontSize: '0.85rem', color: 'var(--text-secondary)',
              }}>
                <McIcon materialKey={typeIcon(e.change_type)} size={14} />
                <span>
                  {e.entity_link
                    ? <Link to={e.entity_link} style={{ color: 'var(--teal)', textDecoration: 'none' }}>{e.entity_name || e.summary}</Link>
                    : (e.entity_name ? <><strong>{e.entity_name}</strong> — </> : null)
                  }
                  {e.entity_name && e.entity_link ? ` — ${e.summary}` : (!e.entity_link ? e.summary : null)}
                </span>
              </div>
            ))}
            {entries.length > 4 && (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>+{entries.length - 4} more changes</div>
            )}
          </div>
        )}

        <div style={{ marginTop: '1rem', paddingTop: '0.8rem', borderTop: '1px solid var(--border)' }}>
          <Link
            to={`/wiki/updates/${v.id}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              color: 'var(--teal)', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600,
            }}
          >
            View Full Changelog →
          </Link>
        </div>
      </div>
    </div>
  );
}

function typeIcon(t) {
  const m = {
    new_item: 'diamond_sword', update_item: 'anvil', new_mob: 'zombie_head',
    update_mob: 'zombie_head', supply_drop: 'chest', new_mechanic: 'piston',
    edit_mechanic: 'piston', new_feature: 'nether_star', removed_item: 'barrier',
    removed_mob: 'barrier', custom: 'book',
  };
  return m[t] || 'book';
}

export default function WikiUpdates() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');

  useEffect(() => {
    api('getwikiversionsv2').then(res => {
      const all = res.isSuccess ? (res.data || []) : [];
      setVersions(all.filter(v => v.status === 'published'));
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return versions;
    return versions.filter(v =>
      (v.entries || []).some(e => e.tag === filter)
    );
  }, [versions, filter]);

  const heroVideo = versions.find(v => v.video_url)?.video_url || FALLBACK_VIDEO;
  const heroImage = !heroVideo ? (versions.find(v => v.image_url)?.image_url || null) : null;

  return (
    <div className="wiki-detail-root">
      <div className="wiki-detail-hero">
        <div className="wiki-detail-hero-overlay" />
        {heroVideo ? (
          <video
            src={heroVideo}
            autoPlay loop muted playsInline
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              objectFit: 'cover', opacity: 0.3,
            }}
          />
        ) : heroImage ? (
          <img src={heroImage} alt="" style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', opacity: 0.25,
          }} />
        ) : null}
        <FadeIn className="wiki-detail-hero-content container" y={30} duration={0.8}>
          <button className="wiki-back-btn" onClick={() => window.history.back()}>← Back</button>
          <div className="wiki-detail-title-section">
            <div className="wiki-detail-icon-box wmd-icon-box" style={{ '--tier-color': '#f0ab20' }}>
              <McIcon materialKey="book" size={48} />
            </div>
            <div>
              <h1 className="wiki-detail-item-name" style={{ color: '#f0ab20' }}>Patch Notes</h1>
              <div className="wiki-detail-item-type" style={{ color: '#d4920a' }}>
                {versions.length} published update{versions.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>

      <div className="container" style={{ paddingTop: '1.5rem', paddingBottom: '3rem' }}>
        {/* filter bar */}
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {TAGS.map(t => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              style={{
                background: filter === t.value
                  ? (t.color ? `${t.color}22` : 'var(--teal-faint)')
                  : 'var(--bg-raised)',
                color: filter === t.value ? (t.color || 'var(--teal)') : 'var(--text-secondary)',
                border: `1px solid ${filter === t.value ? (t.color || 'var(--teal)') : 'var(--border)'}`,
                borderRadius: 20, padding: '0.35rem 0.9rem',
                cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                transition: 'all 0.15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ color: 'var(--text-muted)', padding: '3rem', textAlign: 'center' }}>Loading updates...</div>
        ) : filtered.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', padding: '3rem', textAlign: 'center' }}>
            {filter === 'all' ? 'No updates published yet.' : 'No updates match this filter.'}
          </div>
        ) : (
          <StaggerContainer style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {filtered.map(v => (
              <StaggerItem key={v.id}>
                <VersionCard v={v} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        )}
      </div>
    </div>
  );
}
